/** Key pickers for Settings › Voice: push-to-talk's key (or mouse side button), and the keybinds' combinations. */
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { Btn } from '../ui/kit';
import { pushEscape } from '../ui/overlay';
import { comboLabel, comboOfEvent, pauseHotkeys } from '../voice/hotkeys';
import { keyLabel, swallowSideButtonNavigation } from '../voice/ptt';

export function KeyCapture({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange(code: string): void;
  label: string;
}) {
  const { t } = useT();
  const [listening, setListening] = useState(false);
  // While listening, Escape cancels the capture only: it sits on top of the
  // overlay Escape stack, which otherwise closes Settings (live bug 2026-10-28).
  // No keybind runs while it listens, as ComboCapture: recording Ctrl+Shift+M
  // as the priority key muted you (QA NV-03).
  useEffect(() => {
    if (!listening) return;
    const offEscape = pushEscape(() => setListening(false));
    const resume = pauseHotkeys();
    return () => {
      offEscape();
      resume();
    };
  }, [listening]);
  return (
    <button
      type="button"
      className={'ns-keycap' + (listening ? ' on' : '')}
      aria-label={label}
      aria-pressed={listening}
      onClick={() => setListening(true)}
      onBlur={() => setListening(false)}
      onKeyDown={(e) => {
        if (!listening) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          setListening(false);
          return;
        }
        if (e.key === 'Tab') return;
        e.preventDefault();
        e.stopPropagation();
        onChange(e.code);
        setListening(false);
      }}
      onMouseDown={(e) => {
        if (listening && e.button >= 3) {
          e.preventDefault();
          // the browser goes back or forward when a side button is released (live bug 2026-10-28)
          swallowSideButtonNavigation(e.button);
          onChange(`Mouse${e.button}`);
          setListening(false);
        }
      }}
    >
      {listening ? (
        t('ns.key.press')
      ) : value ? (
        <kbd className="nx-kbd">{keyLabel(value)}</kbd>
      ) : (
        // an optional key not bound yet (the priority-speaker key, 2026-11-01)
        t('ns.key.none')
      )}
    </button>
  );
}

/**
 * A keybind's combination (Discord's Record keybind): a click listens; the
 * first key that is not a modifier, with the modifiers held, is the
 * combination ("Ctrl + Shift + M"). Escape alone cancels; Clear unbinds.
 * No keybind runs while it listens (it would toggle as you record).
 */
export function ComboCapture({
  value,
  onChange,
  label,
}: {
  value: string | null;
  onChange(combo: string | null): void;
  label: string;
}) {
  const { t } = useT();
  const [listening, setListening] = useState(false);
  useEffect(() => {
    if (!listening) return;
    const offEscape = pushEscape(() => setListening(false));
    const resume = pauseHotkeys();
    return () => {
      offEscape();
      resume();
    };
  }, [listening]);
  return (
    <span className="ns-combo">
      <button
        type="button"
        className={'ns-keycap' + (listening ? ' on' : '')}
        aria-label={`${label}: ${value ? comboLabel(value) : t('ns.key.none')}. ${t('ns.key.record')}`}
        aria-pressed={listening}
        data-combo={value ?? ''}
        onClick={() => setListening(true)}
        onBlur={() => setListening(false)}
        onKeyDown={(e) => {
          if (!listening) return;
          const bare = !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey;
          if (e.key === 'Escape' && bare) {
            e.preventDefault();
            e.stopPropagation();
            setListening(false);
            return;
          }
          if (e.key === 'Tab' && !e.ctrlKey && !e.altKey && !e.metaKey) return;
          e.preventDefault();
          e.stopPropagation();
          const combo = comboOfEvent(e.nativeEvent);
          // a modifier alone: keep listening for the key
          if (!combo) return;
          onChange(combo);
          setListening(false);
        }}
      >
        {listening ? (
          t('ns.key.pressCombo')
        ) : value ? (
          <kbd className="nx-kbd">{comboLabel(value)}</kbd>
        ) : (
          t('ns.key.none')
        )}
      </button>
      {value ? (
        <Btn kind="quiet" size="sm" data-combo-clear="" onClick={() => onChange(null)}>
          {t('ns.key.clear')}
        </Btn>
      ) : null}
    </span>
  );
}
